import dayjs from 'dayjs';
import { DateTime } from 'luxon';
import * as R from 'ramda';
import { RRule, rrulestr } from 'rrule';
import { newPortalDate } from './PortalDate';
import { RepeatType } from '@/API';
import {
  ScheduledEventMetadataWithDetailForFront,
  ScheduledHolidayMetadataWithDetailForFront,
} from '@/types/type';
import { TZIDLocations } from '@/utils/consts';

const END_OF_DAY_HOURS = 23;
const END_OF_DAY_MINS = 59;
const END_OF_DAY_SECS = 59;
const END_OF_DAY = '23:59:59';

//TODO:Change to portalDate #781
export const getRRuleSet = (data: {
  repeatType: RepeatType;
  dateTo: string;
  dateFrom: string;
  selectedDays: number[];
  selectedWeekdays: number[];
  executionTime?: string;
}) => {
  let rRule!: RRule;
  const dateToInUTC = data.dateTo
    ? getDateInUTCWithSameDateTime(data.dateTo, END_OF_DAY)
    : getDateInUTCWithSameDateTime(
        getFormattedDateWithoutTime(
          newPortalDate(dayjs(data.dateFrom))
            .add(1, 'day')
            .toDate()
        ),
        END_OF_DAY
      );

  const dateFromInUTC = getDateInUTCWithSameDateTime(
    data.dateFrom,
    data.executionTime
  );

  switch (data.repeatType) {
    case RepeatType.NoRepeat:
      rRule = getRRuleSetNoRepeat(dateFromInUTC, dateToInUTC);
      break;
    case RepeatType.Daily:
      rRule = getRRuleSetByDaily(dateFromInUTC, dateToInUTC);
      break;
    case RepeatType.Monthly:
      rRule = getRRuleSetByMonthly(
        dateFromInUTC,
        dateToInUTC,
        data.selectedDays
      );
      break;
    case RepeatType.Weekly:
      rRule = getRRuleSetByWeekly(
        dateFromInUTC,
        dateToInUTC,
        data.selectedWeekdays
      );
      break;
    case RepeatType.FirstDayOfMonth:
      rRule = getRRuleSetByFirstDayOfMonth(dateFromInUTC, dateToInUTC);
      break;
    case RepeatType.LastDayOfMonth:
      rRule = getRRuleSetByLastDayOfMonth(dateFromInUTC, dateToInUTC);
      break;
    default: {
      const inspector: never = data.repeatType;
      break;
    }
  }
  return rRule.toString();
};

export const getEndOfDayInUTC = (date: string) => {
  const dateFromArg = new Date(date);

  const dateInUTC = new Date(
    Date.UTC(
      dateFromArg.getFullYear(),
      dateFromArg.getMonth(),
      dateFromArg.getDate(),
      END_OF_DAY_HOURS,
      END_OF_DAY_MINS,
      END_OF_DAY_SECS
    )
  );
  return dateInUTC;
};

export const getDateInUTCWithSameDateTime = (
  date: string,
  time: string = '00:00:00'
) => {
  const dateFromArg = new Date(R.join(' ', [date, time]));

  const dateInUTC = new Date(
    Date.UTC(
      dateFromArg.getFullYear(),
      dateFromArg.getMonth(),
      dateFromArg.getDate(),
      dateFromArg.getHours(),
      dateFromArg.getMinutes(),
      dateFromArg.getSeconds()
    )
  );
  return dateInUTC;
};

const getRRuleSetNoRepeat = (dateFromInUTC: Date, dateToInUTC: Date) => {
  return new RRule({
    dtstart: dateFromInUTC,
    until: dateToInUTC,
    tzid: TZIDLocations.UTC9,
    freq: RRule.DAILY,
    count: 1,
  });
};

const getRRuleSetByDaily = (dateFromInUTC: Date, dateToInUTC: Date) => {
  return new RRule({
    dtstart: dateFromInUTC,
    until: dateToInUTC,
    tzid: TZIDLocations.UTC9,
    freq: RRule.DAILY,
  });
};

const getRRuleSetByMonthly = (
  dateFromInUTC: Date,
  dateToInUTC: Date,
  selectedDays: number[]
) => {
  return new RRule({
    dtstart: dateFromInUTC,
    until: dateToInUTC,
    tzid: TZIDLocations.UTC9,
    freq: RRule.MONTHLY,
    bymonthday: selectedDays,
  });
};

const getRRuleSetByWeekly = (
  dateFromInUTC: Date,
  dateToInUTC: Date,
  selectedWeekdays: number[]
) => {
  return new RRule({
    dtstart: dateFromInUTC,
    until: dateToInUTC,
    tzid: TZIDLocations.UTC9,
    freq: RRule.WEEKLY,
    byweekday: selectedWeekdays,
  });
};

const getRRuleSetByFirstDayOfMonth = (
  dateFromInUTC: Date,
  dateToInUTC: Date
) => {
  return new RRule({
    dtstart: dateFromInUTC,
    until: dateToInUTC,
    tzid: TZIDLocations.UTC9,
    freq: RRule.MONTHLY,
    bymonthday: [1],
  });
};

const getRRuleSetByLastDayOfMonth = (
  dateFromInUTC: Date,
  dateToInUTC: Date
) => {
  return new RRule({
    dtstart: dateFromInUTC,
    until: dateToInUTC,
    tzid: TZIDLocations.UTC9,
    freq: RRule.MONTHLY,
    bymonthday: [28, 29, 30, 31],
    bysetpos: -1,
  });
};

export const getFormattedDateWithoutTimeInUtc = (date: Date) =>
  date.toISOString().split('T')[0];

export const getFormattedDateWithoutTime = (date: Date) =>
  newPortalDate(dayjs(date), 'YYYY-MM-DD').toString();

export const getFormattedTime = (date: Date) => {
  return (
    date
      .getUTCHours()
      .toString()
      .padStart(2, '0') +
    ':' +
    date
      .getUTCMinutes()
      .toString()
      .padStart(2, '0')
  );
};

export const parseRRule = (rruleString: string) => {
  const rrule: RRule = rrulestr(rruleString);

  const dtstart: Date = rrule.options.dtstart;
  const until: Date = R.propOr(dtstart, 'until')(rrule.options);

  const dateFrom = getFormattedDateWithoutTimeInUtc(dtstart);
  const executionTime = getFormattedTime(dtstart);
  const dateTo = getFormattedDateWithoutTimeInUtc(until);
  const selectedWeekdays = R.defaultTo([], rrule.options.byweekday);
  const selectedDays = R.defaultTo([], rrule.options.bymonthday);

  return { dateFrom, dateTo, executionTime, selectedWeekdays, selectedDays };
};

export const getRRuleSetArgs: (
  x:
    | ScheduledEventMetadataWithDetailForFront
    | ScheduledHolidayMetadataWithDetailForFront
) => any = R.pickAll([
  'repeatType',
  'dateTo',
  'dateFrom',
  'selectedDays',
  'selectedWeekdays',
  'executionTime',
]);
